import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, ClipboardCheck, Trash2, UtensilsCrossed, ShoppingCart, Check, ChevronRight, Clock, Lock, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, PageLoader } from "../lib/ui";
import { useT } from "../lib/i18n";
import { setGuidedReturn } from "../components/GuidedReturnBanner";

const ICON = { stock_take: ClipboardCheck, waste: Trash2, staff_meal: UtensilsCrossed, order: ShoppingCart };
const NO_ROUTES = ["/opname", "/waste", "/employee-meals", "/orders"];

export default function NightOps() {
  const { t } = useT();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/workspace/night-ops");
      setData(data); setDenied(false);
    } catch (e) { if (e.response?.status === 400 || e.response?.status === 403) setDenied(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (denied) return <Card className="m-4 p-6 text-center text-sm text-ink-mute" data-testid="night-ops-denied">{t("Night Ops is for branch accounts. Select a branch from the dashboard.")}</Card>;
  if (!data) return <PageLoader />;

  const openStep = async (s) => {
    setGuidedReturn("/night-ops", "Night Ops", NO_ROUTES);
    if (s.key === "order") {
      if (s.order_id) { navigate(`/orders/${s.order_id}/edit`); return; }
      // F1 — Smart suggestion: prefill a DRAFT from low-stock items (fully editable; the
      // Head Chef still reviews & submits). Reuses the existing cf_order_prefill mechanism.
      try {
        const { data: inv } = await api.get("/branch/my-inventory");
        const pre = (inv || [])
          .filter((it) => ["CRITICAL", "LOW"].includes(it.status) ||
            (Number(it.current_stock) || 0) <= (Number(it.minimum_stock) || 0))
          .map((it) => ({
            product_id: it.product_id,
            qty: Math.max(0, Math.round(((Number(it.ideal_stock) || 0) - (Number(it.current_stock) || 0)) * 100) / 100),
          }))
          .filter((e) => e.product_id && e.qty > 0);
        localStorage.setItem("cf_order_prefill", JSON.stringify(pre));
      } catch (e) { /* fall back to a blank order */ }
      navigate("/orders/new");
      return;
    }
    navigate(s.route);
  };

  const pct = data.total_required ? Math.round((data.completed_required / data.total_required) * 100) : 0;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="night-ops">
      {/* Hero */}
      <div className="mb-5 rounded-3xl bg-gradient-to-br from-indigo-900 via-violet-900 to-slate-900 p-6 text-white shadow-card">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15"><Moon size={24} /></span>
          <div>
            <p className="font-heading text-2xl font-extrabold leading-tight">{t("Tonight's Tasks")}</p>
            <p className="text-sm text-white/70">{data.branch_name} · {data.cycle_date}</p>
          </div>
          <div className="ml-auto text-right">
            <Badge tone={data.past_cutoff ? "danger" : "info"} data-testid="no-cutoff">
              <Clock size={12} /> {t("Cutoff")} {data.cutoff_time}
            </Badge>
          </div>
        </div>
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-xs font-bold text-white/80">
            <span>{t("Progress")}</span><span data-testid="no-progress">{data.completed_required}/{data.total_required} {t("required done")}</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/20" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-300 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {/* All-done banner */}
      {data.all_done && (
        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="no-alldone">
          <CheckCircle2 size={22} className="text-success-text" />
          <p className="text-sm text-ink-soft">
            <b className="text-ink">{t("All set for tonight!")}</b> {t("Your order has been handed over to")} <b>{t("Central Purchasing")}</b>.
          </p>
        </div>
      )}

      {/* Guided checklist */}
      <div className="space-y-3">
        {data.steps.map((s, i) => {
          const Icon = ICON[s.key] || Check;
          return (
            <Card key={s.key} className={`flex items-center gap-4 p-4 transition ${s.done ? "border-emerald-200 bg-emerald-50/40" : "hover:shadow-lg"}`} data-testid={`no-step-${s.key}`}>
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${s.done ? "bg-success text-white" : "bg-primary-light text-primary"}`}>
                {s.done ? <Check size={22} strokeWidth={3} /> : <Icon size={20} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold text-stone-400">{i + 1}</span>
                  <p className="font-heading text-base font-extrabold text-ink">{t(s.label)}</p>
                  {!s.required && <Badge tone="mute">{t("optional")}</Badge>}
                  {s.late && <Badge tone="danger">{t("Late")}</Badge>}
                </div>
                <p className="text-xs text-ink-mute">
                  {s.done ? t("Completed") : t("Not done yet")}
                  {s.key === "order" && s.status ? ` · ${t(s.status)}` : ""}
                  {s.key === "waste" && s.count ? ` · ${s.count}` : ""}
                </p>
              </div>
              <Button variant={s.done ? "outline" : "primary"} size="sm" onClick={() => openStep(s)} data-testid={`no-open-${s.key}`}>
                {s.done ? t("Review") : (s.key === "order" ? t("Prepare Order") : t("Open"))} <ChevronRight size={15} />
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Handoff footer */}
      <div className="mt-5 flex items-center justify-center gap-2 rounded-2xl border border-dashed border-line bg-app py-3 text-sm text-ink-soft" data-testid="no-handoff">
        {t("Next")}: <b className="text-ink">{t("Central Purchasing")}</b> <ArrowRight size={15} className="text-primary" />
      </div>
    </div>
  );
}
